import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  X,
  Maximize,
  RotateCcw,
  Eye,
  Tags,
  Unlink,
  Search,
  Star,
  Pencil,
  Trash2,
  Waypoints,
  Link2,
  ChevronUp,
  Minus,
  Globe,
  Activity,
  Box,
} from "lucide-react";
import DateRangePicker from "../../../Universal/DateRangePicker";
import { safeIcon, graphViewIcon } from "../config/graphIcons";

const GraphBottomBar = memo(
  ({
    t,
    dark,
    datePickerTheme,
    graphSourceLabel,
    graphSourceMeta,
    activeGraphUuid,
    connectedTypes,
    teamMembers,
    customViews,
    switchToMyGraph,
    switchToTeamGraph,
    switchToGlobalGraph,
    handleControlsSelect,
    applyCustomView,
    isFavorited,
    toggleFavorite,
    deleteGraphView,
    loadGraphViewsData,
    barSearch,
    onSearchChange,
    onExecuteSearch,
    onClearSearch,
    onClearAllFilters,
    searchMatchedNodes,
    showEdgeLabels,
    setShowEdgeLabels,
    manualOnly,
    setManualOnly,
    hideOrphans,
    setHideOrphans,
    degreeHighlight,
    setDegreeHighlight,
    graphDateFrom,
    graphDateTo,
    setGraphDateFrom,
    setGraphDateTo,
    graphLimit,
    setGraphLimit,
    refetchWithLimit,
    graphStats,
    backendTotals,
    handleZoomToFit,
    handleRelayout,
    handleZoomToNode,
    onOpenCreateView,
    onOpenEditView,
    is3D,
    setIs3D,
  }) => {
    // ── Design tokens ─────────────────────────────────────────────────────────
    const cyan = "14,165,233";
    const cyanLit = "56,189,248";

    // Single source of truth for all borders — no mixing Tailwind + inline
    const border = dark ? `rgba(${cyan},0.38)` : `rgba(100,116,139,0.28)`;
    const cyanText = dark ? `rgb(${cyanLit})` : `rgb(${cyan})`;
    const muted = dark ? `rgba(148,163,184,0.65)` : `rgba(71,85,105,0.80)`;
    const hoverBg = dark ? `rgba(30,50,72,0.50)` : `rgba(192,203,218,0.50)`;
    const activeBg = dark ? `rgba(20,34,52,0.80)` : `rgba(191,202,219,0.50)`;
    const inputBg = dark ? `rgba(17,24,39,0.70)` : `rgba(241,245,249,0.90)`;
    const inputText = dark ? `rgba(226,232,240,0.90)` : `rgba(15,23,42,0.90)`;
    const starActiveColor = "rgba(245,158,11,0.85)";

    // Inner glow for the panel itself (dark only)
    const innerGlow = dark
      ? `radial-gradient(ellipse 70% 60% at 50% 0%, rgba(${cyan},0.07) 0%, transparent 100%)`
      : "none";

    // ── Ghost button style — used by ALL buttons (source, toggles, actions)
    // Inputs (search, limit) get a filled style separately.
    const ghostBtn = (active = false) => ({
      display: "flex",
      alignItems: "center",
      gap: 5,
      padding: "7px 12px",
      borderRadius: 8,
      fontSize: 12,
      cursor: "pointer",
      transition: "all 0.15s",
      background: active ? `rgba(${cyan},0.10)` : "transparent",
      border: `1px solid ${active ? `rgba(${cyan},0.40)` : border}`,
      color: active ? cyanText : muted,
      boxShadow:
        active && dark
          ? `0 0 12px rgba(${cyan},0.16), inset 0 0 8px rgba(${cyan},0.05)`
          : "none",
      textShadow: active && dark ? `0 0 8px rgba(${cyanLit},0.50)` : "none",
    });

    // ── Filled input style — search bar, limit input
    const filledInput = {
      background: inputBg,
      border: `1px solid ${border}`,
      color: inputText,
      borderRadius: 8,
      outline: "none",
      transition: "border-color 0.15s",
    };

    const [graphSourceOpen, setGraphSourceOpen] = useState(false);
    const [graphSourceSearch, setGraphSourceSearch] = useState("");
    const [editingLimit, setEditingLimit] = useState(false);

    const limitTimerRef = useRef(null);
    const pendingLimitRef = useRef(graphLimit);
    const changeLimitBy = (delta) => {
      const v = Math.max(1, Math.min(5000, pendingLimitRef.current + delta));
      pendingLimitRef.current = v;
      setGraphLimit(v);
      if (limitTimerRef.current) clearTimeout(limitTimerRef.current);
      limitTimerRef.current = setTimeout(
        () => refetchWithLimit(pendingLimitRef.current),
        500,
      );
    };

    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimerRef = useRef(null);
    const graphSourceRef = useRef(null);
    const [localSearchText, setLocalSearchText] = useState(barSearch);

    useEffect(() => {
      setLocalSearchText(barSearch);
      if (!barSearch) {
        setSearchResults([]);
        setSearchLoading(false);
      }
    }, [barSearch]);

    useEffect(() => {
      if (!graphSourceOpen) return;
      const handler = (e) => {
        if (
          graphSourceRef.current &&
          !graphSourceRef.current.contains(e.target)
        )
          setGraphSourceOpen(false);
      };
      document.addEventListener("pointerdown", handler, true);
      return () => document.removeEventListener("pointerdown", handler, true);
    }, [graphSourceOpen]);

    const handleLocalSearchChange = useCallback(
      (value) => {
        setLocalSearchText(value);
        onSearchChange(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (!value.trim()) {
          setSearchResults([]);
          setSearchLoading(false);
          searchTimerRef.current = setTimeout(() => onClearSearch(), 300);
          return;
        }
        setSearchLoading(true);
        searchTimerRef.current = setTimeout(() => {
          onExecuteSearch(value, (results) => {
            setSearchResults(results || []);
            setSearchLoading(false);
          });
        }, 400);
      },
      [onSearchChange, onClearSearch, onExecuteSearch],
    );

    const handleSearchKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" && localSearchText.trim()) {
          if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
          setSearchLoading(true);
          onExecuteSearch(localSearchText, (results) => {
            setSearchResults(results || []);
            setSearchLoading(false);
          });
        }
        if (e.key === "Escape") {
          setLocalSearchText("");
          setSearchResults([]);
          setSearchLoading(false);
          onClearSearch();
        }
      },
      [localSearchText, onExecuteSearch, onClearSearch],
    );

    const handleClearSearch = useCallback(() => {
      setLocalSearchText("");
      setSearchResults([]);
      setSearchLoading(false);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      onClearSearch();
    }, [onClearSearch]);

    const wrapSourceSwitch = useCallback(
      (fn) =>
        (...args) => {
          setGraphSourceOpen(false);
          setSearchResults([]);
          setLocalSearchText("");
          fn(...args);
        },
      [],
    );

    // Vertical divider between row sections
    const Divider = () => (
      <div
        style={{
          width: 1,
          height: 20,
          background: border,
          opacity: 0.7,
          flexShrink: 0,
        }}
      />
    );

    // Dropdown section label
    const DropLabel = ({ children, className = "" }) => (
      <div
        className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${className}`}
        style={{ color: muted }}
      >
        {children}
      </div>
    );

    return (
      <>
        <style>{`
        @keyframes arwes-glow-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.08),
              0 0 14px 2px rgba(${cyan},0.10),
              0 0 36px 4px rgba(${cyan},0.04),
              var(--ps);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.16),
              0 0 20px 4px rgba(${cyan},0.17),
              0 0 50px 7px rgba(${cyan},0.07),
              var(--ps);
          }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .gb-input::placeholder { color: ${muted}; }
        .gb-input:focus { border-color: rgba(${cyan},0.45) !important; }
      `}</style>

        <div className="pointer-events-none absolute bottom-4 left-5 right-5 z-[55] flex flex-col items-center">
          <motion.div
            className="pointer-events-auto relative w-full max-w-[1100px] overflow-hidden rounded-lg backdrop-blur-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.1, 0.25, 1],
              delay: 0.1,
            }}
            style={{
              background:
                innerGlow !== "none"
                  ? `${innerGlow}, ${t.surface.bgSolid}`
                  : t.surface.bgSolid,
              border: `1px solid ${border}`,
              "--ps": t.surface.shadow,
              animation: "arwes-glow-pulse 5s ease-in-out infinite",
            }}
          >
            {/* Top accent line — dark only, softened */}
            {dark && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 0,
                  left: "20%",
                  right: "20%",
                  height: 1,
                  pointerEvents: "none",
                  zIndex: 1,
                  background: `linear-gradient(90deg, transparent, rgba(${cyan},0.22) 30%, rgba(${cyanLit},0.40) 50%, rgba(${cyan},0.22) 70%, transparent)`,
                  boxShadow: `0 0 5px 1px rgba(${cyan},0.14)`,
                }}
              />
            )}

            <div className="relative z-[1]">
              {/* ── Row 1 ── */}
              <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                {/* Graph source button — ghost style, same as filter toggles */}
                <div
                  className="relative"
                  ref={graphSourceRef}
                  data-tutorial="graph-source"
                >
                  <button
                    style={{
                      ...ghostBtn(graphSourceOpen),
                      paddingLeft: 10,
                      paddingRight: 10,
                    }}
                    onClick={() => setGraphSourceOpen(!graphSourceOpen)}
                  >
                    {React.createElement(Waypoints, {
                      size: 13,
                      style: { color: cyanText, opacity: 0.8, flexShrink: 0 },
                    })}
                    <span className="max-w-[120px] truncate">
                      {graphSourceLabel}
                    </span>
                    {React.createElement(ChevronUp, {
                      size: 12,
                      style: {
                        color: cyanText,
                        opacity: 0.55,
                        transform: graphSourceOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.15s ease",
                        flexShrink: 0,
                      },
                    })}
                  </button>

                  {/* Graph source dropdown */}
                  {graphSourceOpen &&
                    (() => {
                      const srcQ = graphSourceSearch.trim().toLowerCase();
                      const filtered = srcQ
                        ? connectedTypes.filter((ct) =>
                            ct.value.toLowerCase().includes(srcQ),
                          )
                        : connectedTypes;
                      const nodeTypes = filtered.filter(
                        (ct) => ct.type === "node",
                      );
                      const linkTypes2 = filtered.filter(
                        (ct) => ct.type === "link",
                      );
                      const filteredTeam = srcQ
                        ? teamMembers.filter((m) =>
                            `${m.first_name} ${m.last_name}`
                              .toLowerCase()
                              .includes(srcQ),
                          )
                        : teamMembers;

                      return (
                        <div
                          className="absolute bottom-full left-0 mb-2 w-[280px] overflow-hidden rounded-lg backdrop-blur-lg"
                          style={{
                            background: t.surface.popupBg,
                            border: `1px solid ${border}`,
                            boxShadow: `${t.surface.popupShadow}${dark ? `, 0 0 22px rgba(${cyan},0.14)` : ""}`,
                          }}
                        >
                          <div className="p-2 pb-0">
                            <div className="relative">
                              {React.createElement(Search, {
                                size: 12,
                                style: {
                                  position: "absolute",
                                  left: 8,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  opacity: 0.4,
                                },
                              })}
                              <input
                                type="text"
                                autoFocus
                                className="gb-input w-full rounded-lg py-1.5 pl-7 pr-3 text-xs outline-none"
                                style={filledInput}
                                placeholder="Filter sources..."
                                value={graphSourceSearch}
                                onChange={(e) =>
                                  setGraphSourceSearch(e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div
                            className="max-h-[320px] overflow-y-auto"
                            style={{ scrollbarWidth: "thin" }}
                            ref={(el) => {
                              if (el && !el.dataset.scrolled) {
                                el.scrollTop = el.scrollHeight;
                                el.dataset.scrolled = "1";
                              }
                            }}
                          >
                            {linkTypes2.length > 0 && (
                              <>
                                <DropLabel>Links</DropLabel>
                                {linkTypes2.map((item) => {
                                  const starred = isFavorited(
                                    "edge_type",
                                    item.value,
                                  );
                                  return (
                                    <button
                                      key={`link-${item.value}`}
                                      className="group/src w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition"
                                      style={{
                                        background: "transparent",
                                        color: inputText,
                                        cursor: "pointer",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background =
                                          hoverBg;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background =
                                          "transparent";
                                      }}
                                      onClick={() => {
                                        setGraphSourceOpen(false);
                                        handleControlsSelect(item);
                                      }}
                                    >
                                      {React.createElement(Link2, {
                                        size: 13,
                                        style: { opacity: 0.5, flexShrink: 0 },
                                      })}
                                      <span className="flex-1 truncate">
                                        {item.value
                                          .replace(/_/g, " ")
                                          .toLowerCase()}
                                      </span>
                                      <span
                                        className={`shrink-0 transition-opacity ${starred ? "opacity-100" : "opacity-0 group-hover/src:opacity-100"}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleFavorite(
                                            "edge_type",
                                            item.value,
                                            item.value
                                              .replace(/_/g, " ")
                                              .toLowerCase(),
                                          );
                                        }}
                                      >
                                        {React.createElement(Star, {
                                          size: 12,
                                          style: {
                                            fill: starred
                                              ? starActiveColor
                                              : "none",
                                            color: starred
                                              ? starActiveColor
                                              : muted,
                                          },
                                        })}
                                      </span>
                                    </button>
                                  );
                                })}
                              </>
                            )}

                            <DropLabel
                              className={linkTypes2.length > 0 ? "mt-1" : ""}
                            >
                              Nodes
                            </DropLabel>
                            {nodeTypes.length === 0 &&
                              linkTypes2.length === 0 && (
                                <div
                                  className="px-3 py-2 text-xs"
                                  style={{ color: muted }}
                                >
                                  No matches
                                </div>
                              )}
                            {nodeTypes.map((item) => {
                              const IconC = safeIcon(item.value);
                              const starred = isFavorited(
                                "node_type",
                                item.value,
                              );
                              return (
                                <button
                                  key={`node-${item.value}`}
                                  className="group/src w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition"
                                  style={{
                                    background: "transparent",
                                    color: inputText,
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = hoverBg;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      "transparent";
                                  }}
                                  onClick={() => {
                                    setGraphSourceOpen(false);
                                    handleControlsSelect(item);
                                  }}
                                >
                                  {React.createElement(IconC, {
                                    size: 13,
                                    style: {
                                      color:
                                        t.node.typeColors?.[item.value] ||
                                        t.node.iconColor,
                                      flexShrink: 0,
                                    },
                                  })}
                                  <span className="flex-1 truncate">
                                    {item.value
                                      .replace(/_/g, " ")
                                      .toLowerCase()}
                                  </span>
                                  <span
                                    className={`shrink-0 transition-opacity ${starred ? "opacity-100" : "opacity-0 group-hover/src:opacity-100"}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(
                                        "node_type",
                                        item.value,
                                        item.value
                                          .replace(/_/g, " ")
                                          .toLowerCase(),
                                      );
                                    }}
                                  >
                                    {React.createElement(Star, {
                                      size: 12,
                                      style: {
                                        fill: starred
                                          ? starActiveColor
                                          : "none",
                                        color: starred
                                          ? starActiveColor
                                          : muted,
                                      },
                                    })}
                                  </span>
                                </button>
                              );
                            })}

                            <div
                              style={{
                                height: 1,
                                background: border,
                                margin: "4px 12px",
                              }}
                            />
                            <DropLabel>Scope</DropLabel>
                            <button
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition"
                              style={{
                                background:
                                  graphSourceMeta.current?.kind === "global"
                                    ? activeBg
                                    : "transparent",
                                color: inputText,
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                if (graphSourceMeta.current?.kind !== "global")
                                  e.currentTarget.style.background = hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                if (graphSourceMeta.current?.kind !== "global")
                                  e.currentTarget.style.background =
                                    "transparent";
                              }}
                              onClick={wrapSourceSwitch(switchToGlobalGraph)}
                            >
                              {React.createElement(Globe, {
                                size: 13,
                                style: { opacity: 0.5, flexShrink: 0 },
                              })}
                              <span className="flex-1 truncate">
                                Global (all public)
                              </span>
                            </button>

                            {teamMembers.length > 0 && (
                              <>
                                <DropLabel className="mt-1">Team</DropLabel>
                                {filteredTeam.map((m) => (
                                  <button
                                    key={m.uuid}
                                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition"
                                    style={{
                                      background:
                                        graphSourceMeta.current?.kind ===
                                          "team" &&
                                        graphSourceMeta.current?.uuid === m.uuid
                                          ? activeBg
                                          : "transparent",
                                      color: inputText,
                                      cursor: "pointer",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        hoverBg;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        graphSourceMeta.current?.kind ===
                                          "team" &&
                                        graphSourceMeta.current?.uuid === m.uuid
                                          ? activeBg
                                          : "transparent";
                                    }}
                                    onClick={wrapSourceSwitch(() =>
                                      switchToTeamGraph(m),
                                    )}
                                  >
                                    <div
                                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                                      style={{
                                        background: dark
                                          ? "rgba(14,165,233,0.15)"
                                          : "rgba(14,165,233,0.10)",
                                        color: dark
                                          ? "rgba(56,189,248,0.8)"
                                          : "rgba(2,132,199,0.8)",
                                      }}
                                    >
                                      {(m.first_name?.[0] || "?").toUpperCase()}
                                    </div>
                                    <span className="flex-1 truncate">
                                      {m.first_name} {m.last_name}
                                    </span>
                                  </button>
                                ))}
                              </>
                            )}

                            {customViews.length > 0 && (
                              <>
                                <div
                                  style={{
                                    height: 1,
                                    background: border,
                                    margin: "4px 12px",
                                  }}
                                />
                                <DropLabel>Saved Views</DropLabel>
                                {customViews
                                  .filter(
                                    (view) =>
                                      !srcQ ||
                                      view.name.toLowerCase().includes(srcQ),
                                  )
                                  .map((view) => {
                                    const ViewIcon = graphViewIcon(view.icon);
                                    const isActive =
                                      graphSourceMeta.current?.kind ===
                                        "custom_view" &&
                                      graphSourceMeta.current?.view
                                        ?.document_id === view.document_id;
                                    return (
                                      <div
                                        key={view.document_id}
                                        className="group/view flex w-full items-center gap-2 px-3 py-1.5 text-xs transition"
                                        style={{
                                          background: isActive
                                            ? activeBg
                                            : "transparent",
                                          color: inputText,
                                          cursor: "pointer",
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!isActive)
                                            e.currentTarget.style.background =
                                              hoverBg;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background =
                                            isActive ? activeBg : "transparent";
                                        }}
                                        onClick={() => {
                                          setGraphSourceOpen(false);
                                          applyCustomView(view);
                                        }}
                                      >
                                        {React.createElement(ViewIcon, {
                                          size: 13,
                                          style: {
                                            color:
                                              view.color ||
                                              `rgba(${cyan},0.85)`,
                                            flexShrink: 0,
                                          },
                                        })}
                                        <span className="flex-1 truncate">
                                          {view.name}
                                        </span>
                                        <span className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover/view:opacity-100">
                                          <button
                                            className="cursor-pointer rounded p-0.5 transition hover:bg-mainButtonBg/20"
                                            title="Edit"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setGraphSourceOpen(false);
                                              onOpenEditView(view);
                                            }}
                                          >
                                            {React.createElement(Pencil, {
                                              size: 11,
                                              style: { opacity: 0.5 },
                                            })}
                                          </button>
                                          <button
                                            className="cursor-pointer rounded p-0.5 transition hover:bg-mainButtonBg/20"
                                            title="Delete"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (
                                                window.confirm(
                                                  `Delete view "${view.name}"?`,
                                                )
                                              ) {
                                                try {
                                                  await deleteGraphView({
                                                    document_id:
                                                      view.document_id,
                                                  });
                                                  loadGraphViewsData();
                                                  window.dispatchEvent(
                                                    new CustomEvent(
                                                      "sidebar:refresh-graph-views",
                                                    ),
                                                  );
                                                } catch (err) {
                                                  console.error(
                                                    "Delete view failed:",
                                                    err,
                                                  );
                                                }
                                              }
                                            }}
                                          >
                                            {React.createElement(Trash2, {
                                              size: 11,
                                              style: {
                                                opacity: 0.7,
                                                color: "rgba(239,68,68,0.80)",
                                              },
                                            })}
                                          </button>
                                        </span>
                                      </div>
                                    );
                                  })}
                                <button
                                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition"
                                  style={{
                                    background: "transparent",
                                    color: inputText,
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = hoverBg;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      "transparent";
                                  }}
                                  onClick={() => {
                                    setGraphSourceOpen(false);
                                    onOpenCreateView();
                                  }}
                                >
                                  {React.createElement(Plus, {
                                    size: 13,
                                    style: { opacity: 0.5, flexShrink: 0 },
                                  })}
                                  <span className="truncate">
                                    Create new view…
                                  </span>
                                </button>
                              </>
                            )}

                            {graphSourceMeta.current?.kind !== "my_graph" && (
                              <>
                                <div
                                  style={{
                                    height: 1,
                                    background: border,
                                    margin: "4px 12px",
                                  }}
                                />
                                <button
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium transition"
                                  style={{
                                    background: "transparent",
                                    color: inputText,
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = hoverBg;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      "transparent";
                                  }}
                                  onClick={wrapSourceSwitch(switchToMyGraph)}
                                >
                                  {React.createElement(Activity, {
                                    size: 13,
                                    style: { opacity: 0.5, flexShrink: 0 },
                                  })}
                                  <span className="truncate">
                                    ← Back to My Graph
                                  </span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                </div>

                <Divider />

                {/* Search — filled input, full inline styles, no Tailwind class conflicts */}
                <div
                  className="relative min-w-[260px] max-w-[480px] flex-1"
                  data-tutorial="search-bar"
                >
                  {searchLoading ? (
                    <div
                      style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 13,
                        height: 13,
                        border: `2px solid rgba(${cyan},0.3)`,
                        borderTop: `2px solid rgba(${cyan},0.8)`,
                        borderRadius: "50%",
                        animation: "spin 0.6s linear infinite",
                      }}
                    />
                  ) : (
                    React.createElement(Search, {
                      size: 13,
                      style: {
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        opacity: searchMatchedNodes.size > 0 ? 0.9 : 0.35,
                        color: searchMatchedNodes.size > 0 ? cyanText : muted,
                      },
                    })
                  )}
                  <input
                    type="text"
                    className="gb-input w-full text-xs outline-none"
                    style={{
                      ...filledInput,
                      paddingTop: 7,
                      paddingBottom: 7,
                      paddingLeft: 32,
                      paddingRight: 28,
                      borderColor:
                        searchMatchedNodes.size > 0
                          ? `rgba(${cyan},0.45)`
                          : filledInput.border.split(" ").pop(),
                      boxShadow:
                        searchMatchedNodes.size > 0
                          ? `0 0 12px rgba(${cyan},0.15), inset 0 0 5px rgba(${cyan},0.03)`
                          : "none",
                    }}
                    placeholder={`Search ${graphSourceLabel === "My Graph" ? "my graph" : graphSourceLabel === "Global" ? "all public" : graphSourceLabel.replace("'s Graph", "")}...`}
                    value={localSearchText}
                    onChange={(e) => handleLocalSearchChange(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />

                  {localSearchText && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded p-0.5 transition"
                      style={{ color: muted }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = cyanText;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = muted;
                      }}
                      onClick={handleClearSearch}
                    >
                      {React.createElement(X, { size: 13 })}
                    </button>
                  )}

                  {/* Search results dropdown */}
                  {searchResults.length > 0 && localSearchText && (
                    <div
                      className="absolute bottom-full left-0 mb-2 w-full overflow-hidden rounded-lg backdrop-blur-lg"
                      style={{
                        background: t.surface.popupBg,
                        border: `1px solid ${border}`,
                        boxShadow: `${t.surface.popupShadow}${dark ? `, 0 0 18px rgba(${cyan},0.12)` : ""}`,
                      }}
                    >
                      <div
                        className="max-h-[200px] overflow-y-auto"
                        style={{ scrollbarWidth: "thin" }}
                      >
                        {searchResults.map((m, idx) => {
                          const iconColor =
                            (t.node.typeColors &&
                              t.node.typeColors[m.icon || m.group]) ||
                            t.node.iconColor;
                          return (
                            <button
                              key={m.name + idx}
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition"
                              style={{
                                background: "transparent",
                                color: inputText,
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "transparent";
                              }}
                              onClick={() => handleZoomToNode(m.name)}
                            >
                              {React.createElement(
                                safeIcon(m.icon || m.group),
                                {
                                  size: 13,
                                  style: { color: iconColor, flexShrink: 0 },
                                },
                              )}
                              <span className="truncate">{m.name}</span>
                              <span
                                className="text-[11px]"
                                style={{
                                  color: muted,
                                  marginLeft: "auto",
                                  flexShrink: 0,
                                }}
                              >
                                {(m.icon || m.group || "")
                                  .replace(/_/g, " ")
                                  .toLowerCase()}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <Divider />

                {/* Date range */}
                <DateRangePicker
                  dateFrom={graphDateFrom}
                  dateTo={graphDateTo}
                  onChange={(from, to) => {
                    setGraphDateFrom(from);
                    setGraphDateTo(to);
                  }}
                  t={datePickerTheme}
                  compact
                />

                <Divider />

                {/* Action buttons — ghost style, same weight as everything else */}
                <div
                  className="flex items-center gap-1.5"
                  data-tutorial="action-buttons"
                >
                  {[
                    { icon: Maximize, label: "Fit", action: handleZoomToFit },
                    {
                      icon: RotateCcw,
                      label: "Relayout",
                      action: handleRelayout,
                    },
                  ].map((btn) => (
                    <button
                      key={btn.label}
                      style={ghostBtn(false)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = dark
                          ? `rgba(${cyan},0.50)`
                          : `rgba(${cyan},0.45)`;
                        e.currentTarget.style.color = cyanText;
                        if (dark)
                          e.currentTarget.style.boxShadow = `0 0 10px rgba(${cyan},0.14)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = border;
                        e.currentTarget.style.color = muted;
                        e.currentTarget.style.boxShadow = "none";
                      }}
                      onClick={btn.action}
                    >
                      {React.createElement(btn.icon, { size: 13 })}
                      <span>{btn.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Row 2 ── */}
              <div
                className="flex flex-wrap items-center gap-2 px-5 pb-3"
                data-tutorial="filter-row"
              >
                {[
                  {
                    icon: Eye,
                    label: "Edge Labels",
                    active: showEdgeLabels,
                    action: () => setShowEdgeLabels(!showEdgeLabels),
                  },
                  {
                    icon: Tags,
                    label: "Manual Only",
                    active: manualOnly,
                    action: () => setManualOnly(!manualOnly),
                  },
                  {
                    icon: Unlink,
                    label: "Hide Orphans",
                    active: hideOrphans,
                    action: () => setHideOrphans(!hideOrphans),
                  },
                  {
                    icon: Box,
                    label: "3D",
                    active: !!is3D,
                    action: () => setIs3D(!is3D),
                  },
                ].map((btn) => (
                  <button
                    key={btn.label}
                    style={ghostBtn(btn.active)}
                    onClick={btn.action}
                  >
                    {React.createElement(btn.icon, { size: 13 })}
                    <span>{btn.label}</span>
                  </button>
                ))}

                {/* Degree slider */}
                <div className="ml-2 flex items-center gap-2">
                  <span
                    className="text-[11px] font-medium uppercase tracking-wider"
                    style={{ color: muted }}
                  >
                    Degree ≥
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={10}
                    value={degreeHighlight}
                    onChange={(e) => setDegreeHighlight(+e.target.value)}
                    className="h-1 w-16 cursor-pointer accent-sky-500"
                  />
                  <span
                    className="text-xs"
                    style={{ width: 14, textAlign: "right", color: muted }}
                  >
                    {degreeHighlight}
                  </span>
                </div>

                {/* Clear filters */}
                {(localSearchText ||
                  manualOnly ||
                  hideOrphans ||
                  degreeHighlight > 0) && (
                  <button
                    style={{
                      ...ghostBtn(true),
                      marginLeft: 4,
                      border: `1px solid rgba(${cyan},0.35)`,
                      boxShadow: dark ? `0 0 8px rgba(${cyan},0.12)` : "none",
                    }}
                    onClick={() => {
                      handleClearSearch();
                      onClearAllFilters();
                    }}
                  >
                    {React.createElement(X, { size: 12 })}
                    <span>Clear</span>
                  </button>
                )}

                {/* Limit — pushed right */}
                <div
                  className="ml-auto flex items-center gap-0.5"
                  data-tutorial="limit-control"
                  title="Max nodes returned"
                >
                  <span
                    className="mr-1 text-[11px] font-medium uppercase tracking-wider"
                    style={{ color: muted }}
                  >
                    Limit
                  </span>
                  {editingLimit ? (
                    <input
                      type="number"
                      autoFocus
                      min={1}
                      max={5000}
                      defaultValue={graphLimit}
                      className="gb-input text-xs"
                      style={{ ...filledInput, width: 60, padding: "2px 6px" }}
                      onBlur={(e) => {
                        const v = Math.max(
                          1,
                          Math.min(5000, parseInt(e.target.value, 10) || 100),
                        );
                        pendingLimitRef.current = v;
                        setGraphLimit(v);
                        refetchWithLimit(v);
                        setEditingLimit(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                        if (e.key === "Escape") setEditingLimit(false);
                      }}
                    />
                  ) : (
                    <div className="flex items-center gap-0">
                      <button
                        className="cursor-pointer rounded p-1 transition"
                        style={{ color: muted }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = cyanText;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = muted;
                        }}
                        onClick={() => changeLimitBy(-25)}
                      >
                        {React.createElement(Minus, { size: 12 })}
                      </button>
                      <button
                        className="cursor-pointer rounded px-1 py-0.5 text-xs transition"
                        style={{
                          color: muted,
                          minWidth: 32,
                          textAlign: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = cyanText;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = muted;
                        }}
                        onClick={() => setEditingLimit(true)}
                      >
                        {graphLimit}
                      </button>
                      <button
                        className="cursor-pointer rounded p-1 transition"
                        style={{ color: muted }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = cyanText;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = muted;
                        }}
                        onClick={() => changeLimitBy(25)}
                      >
                        {React.createElement(Plus, { size: 12 })}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </>
    );
  },
);

GraphBottomBar.displayName = "GraphBottomBar";
export default GraphBottomBar;
